"use client"

import type React from "react"

import { useState, useEffect, type SetStateAction } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { formatToPKR } from "@/lib/helper"

interface CashSuggestionProps {
  amountToPay: number
  onPaymentSelected: (amount: number) => void
  isOpen: boolean
  setOpen: React.Dispatch<SetStateAction<boolean>>
}

export default function CashSuggestion({ amountToPay, onPaymentSelected, setOpen, isOpen }: CashSuggestionProps) {
  const [customAmount, setCustomAmount] = useState<string>("")
  const [suggestedAmounts, setSuggestedAmounts] = useState<number[]>([])
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null)
  const [showCollectButton, setShowCollectButton] = useState<boolean>(true)

  // Update suggestions when dialog opens with new amountToPay
  useEffect(() => {
    if (isOpen) {
      console.log({amountToPay})
      const suggestions = generateSuggestions(amountToPay)
      setSuggestedAmounts(suggestions)
      setCustomAmount(amountToPay.toString())
      setShowCollectButton(true)
      // setSelectedAmount()
    }
  }, [isOpen, amountToPay])

  const generateSuggestions = (amount: number): number[] => {
    const denominations = [500, 1000, 5000]
    const suggestions: number[] = [amount]
    const baseAmount = amount

    const addSuggestion = (value: number) => {
      if (value > baseAmount && !suggestions.includes(value)) {
        suggestions.push(value)
      }
    }

    // Round up to the nearest denomination
    for (const denom of denominations) {
      const nextValue = Math.ceil(baseAmount / denom) * denom
      if (nextValue > baseAmount) {
        addSuggestion(nextValue)
      }
    }

    return suggestions.slice(0, 4)
  }

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only numbers
    const value = e.target.value.replace(/[^0-9]/g, "")
    setCustomAmount(value)

    // Update selected amount when custom amount changes
    const numValue = Number.parseFloat(value)
    if (value === "") {
      setSelectedAmount(null)
      setShowCollectButton(true) // Show suggestion buttons when input is empty
    } else if (numValue > 0) {
      setSelectedAmount(numValue)
      setShowCollectButton(false) // Show collect button when valid amount entered
    } else {
      setSelectedAmount(null)
      setShowCollectButton(true)
    }
  }

  const handleSuggestionClick = (amount: number) => {
    setSelectedAmount(amount)
    setCustomAmount(amount.toString())
    onPaymentSelected(amount)
    setOpen(false)
  }

  const handleCollectClick = () => {
    if (selectedAmount !== null) {
      onPaymentSelected(selectedAmount)
      setOpen(false)
    }
  }

  // Calculate change
  const change = selectedAmount !== null ? selectedAmount - amountToPay : 0
  const hasChange = selectedAmount !== null && change > 0

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogContent className="min-w-[500px] max-w-fit">
        <DialogHeader className="w-full">
          <DialogTitle className="flex justify-between items-center ">
            <span className="">Amount to Pay</span>
            <span className="text-lg font-bold px-4">{formatToPKR(amountToPay)}</span>
          </DialogTitle>
          <DialogDescription className="text-base font-medium">Amount Given by Customer</DialogDescription>
        </DialogHeader>

        <DialogDescription className="flex gap-2 justify-end">
          <div className="text-base flex items-center space-x-2 w-[120px]">
            <div className="">RS</div>
            <Input
              type="number"
              value={customAmount}
              onChange={handleCustomAmountChange}
              className="text-right"
              placeholder="0.00"
            />
          </div>

          {showCollectButton
            ? // Show suggestion buttons when no custom amount is entered
              suggestedAmounts.map((amount) => (
                <Button
                  key={amount}
                  variant={"default"}
                  className="text-lg p-2 min-w-fit text-gray-50"
                  onClick={() => handleSuggestionClick(amount)}
                >
                  {formatToPKR(amount)}
                </Button>
              ))
            : // Show collect button when any valid amount is entered
              selectedAmount !== null && (
                <Button variant={"default"} className="text-lg p-2 min-w-fit text-gray-50" onClick={handleCollectClick}>
                  Collect {formatToPKR(selectedAmount)}
                </Button>
              )}
        </DialogDescription>
      </DialogContent>
    </Dialog>
  )
}
